import { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Typography,
  Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DateTimePicker from './DateTimePicker';

interface MotivationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (motivation: string, suggestedTimes?: string[]) => void;
  decision: boolean;
  isTimeOnlyMode?: boolean;
  blockedTimes?: string[]; // Times already discussed that should be blocked
  initialMotivation?: string; // Pre-fill motivation when editing
  initialSuggestedTimes?: string[]; // Pre-fill suggested times when editing
}

export default function MotivationModal({ 
  open, 
  onClose, 
  onSubmit, 
  decision, 
  isTimeOnlyMode = false, 
  blockedTimes = [], 
  initialMotivation = '', 
  initialSuggestedTimes = [] 
}: MotivationModalProps) {
  const { t } = useTranslation();
  const [motivation, setMotivation] = useState(initialMotivation);
  const [suggestedTimes, setSuggestedTimes] = useState<string[]>(initialSuggestedTimes);

  // Update state when initial values change (when modal opens with different values)
  useEffect(() => {
    if (open) {
      setMotivation(initialMotivation);
      setSuggestedTimes(initialSuggestedTimes);
    }
  }, [open, initialMotivation, initialSuggestedTimes]);

  // Calculate how many unique dates have been selected
  const selectedDatesCount = new Set(suggestedTimes.map(time => new Date(time).toISOString().split('T')[0])).size;

  // Get button text based on selected dates count
  const getButtonText = () => {
    if (!isAccept && !isTimeOnlyMode) {
      return t('common.submit', 'Skicka');
    }
    
    if (selectedDatesCount === 0) {
      return isTimeOnlyMode 
        ? t('date_selection.select_minimum_coordinate', 'Välj minst 3 datum för att koordinera')
        : t('date_selection.select_minimum', 'Välj minst 3 datum');
    } else if (selectedDatesCount === 1) {
      return t('date_selection.select_two_more', 'Välj 2 datum till');
    } else if (selectedDatesCount === 2) {
      return t('date_selection.select_one_more', 'Välj 1 datum till');
    } else {
      return isTimeOnlyMode 
        ? t('date_selection.submit_times', 'Skicka tider')
        : t('common.submit', 'Skicka');
    }
  };

  const handleSubmit = () => {
    if (decision) {
      // If accepting, only include suggested times (no motivation)
      onSubmit('', suggestedTimes);
    } else {
      // If declining, just submit motivation
      onSubmit(motivation);
    }
    setMotivation('');
    setSuggestedTimes([]);
  };

  const handleClose = () => {
    setMotivation('');
    setSuggestedTimes([]);
    onClose();
  };


  const handleBackdropClick = () => {
    // Just close when clicking outside, don't submit
    handleClose();
  };

  const isAccept = decision;
  const title = isTimeOnlyMode
    ? t('date_selection.coordinate_title', 'Välj när du kan träffas')
    : isAccept 
    ? t('date_selection.modal_title', 'Välj dina lediga tider')
    : t('motivation.decline_title', 'Varför vill du inte träffa denna person?');

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          handleClose(); // Just close, don't submit
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgb(56,25,30)',
          borderRadius: 3,
          color: 'white'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        color: 'white',
        fontWeight: 'bold',
        fontSize: '1.5rem',
        pb: 2
      }}>
        {title}
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, pb: 2 }}>
        {/* Show date picker when accepting or in time-only mode */}
        {isAccept || isTimeOnlyMode ? (
          <>
            <DateTimePicker 
              suggestedTimes={suggestedTimes}
              onChange={setSuggestedTimes}
              blockedTimes={blockedTimes}
            />
            
          </>
        ) : (
          <>
            <Typography variant="body1" sx={{ 
              mb: 3, 
              color: 'rgba(255,255,255,0.7)', 
              textAlign: 'center',
              fontSize: '1rem'
            }}>
              {t('motivation.optional', 'Detta är valfritt men hjälper oss att förbättra matchningen.')}
            </Typography>
            
            <TextField
              autoFocus
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              placeholder={t('motivation.decline_placeholder', 'Berätta vad som fick dig att inte vilja träffa denna person...')}
              value={motivation}
              onChange={(e) => setMotivation(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                  '& input, & textarea': {
                    color: 'white',
                    '&::placeholder': {
                      color: 'rgba(255,255,255,0.5)',
                      opacity: 1
                    }
                  }
                },
              }}
            />
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        pt: 1, 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 2,
        '& > :not(:first-of-type)': {
          marginLeft: 0
        }
      }}>
        <Button 
          onClick={handleSubmit}
          disabled={(isAccept || isTimeOnlyMode) && selectedDatesCount < 3}
          variant="contained"
          sx={{ 
            bgcolor: (isAccept || isTimeOnlyMode) && selectedDatesCount < 3 ? 'rgba(142, 96, 60, 0.5)' : '#8E603C',
            color: 'white',
            fontWeight: 'bold',
            width: '200px',
            '&:hover': { 
              bgcolor: (isAccept || isTimeOnlyMode) && selectedDatesCount < 3 ? 'rgba(142, 96, 60, 0.5)' : '#76451F',
              color: 'white'
            },
            '&.Mui-disabled': {
              bgcolor: 'rgba(142, 96, 60, 0.5)',
              color: 'rgba(255, 255, 255, 0.7)'
            }
          }}
        >
          {getButtonText()}
        </Button>
        {/* Only show cancel button for decline (reject) scenarios */}
        {!isTimeOnlyMode && !isAccept && (
          <Button 
            onClick={handleClose}
            variant="text"
            sx={{ 
              color: 'rgba(255,255,255,0.7)',
              fontSize: '0.9rem',
              textTransform: 'none',
              width: '200px',
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'white'
              }
            }}
          >
            {t('common.cancel', 'Avbryt')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
} 
